// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  getArticles, 
  addArticle, 
  updateArticle, 
  deleteArticle
} from '../firebase';

const Admin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState('');
  
  // Article form state
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    author: 'Kohla News Team',
    excerpt: '',
    content: '',
    tags: '',
    isBreaking: false,
    imageUrl: ''
  });

  // Load articles on mount
  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await getArticles();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle content editor change
  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
  };

  // Handle form submit (Add or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('⏳ Publishing...');

    try {
      // Generate slug from title
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      const articleData = {
        title: formData.title,
        slug,
        category: formData.category,
        author: formData.author,
        excerpt: formData.excerpt,
        content: formData.content,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isBreaking: formData.isBreaking,
        image: formData.imageUrl || '/images/default.jpg',
        date: new Date().toISOString().split('T')[0],
        readTime: `${Math.ceil(formData.content.length / 1500)} min read`
      };

      if (editingId) {
        await updateArticle(editingId, articleData);
        setStatus('✅ Article updated successfully!');
      } else {
        await addArticle(articleData);
        setStatus('✅ Article published successfully!');
      }

      // Reset form
      resetForm();
      loadArticles();
    } catch (error) {
      setStatus('❌ Error: ' + error.message);
    }
  };

  // Edit article
  const handleEdit = (article) => {
    setEditingId(article.id);
    setFormData({
      title: article.title,
      category: article.category,
      author: article.author,
      excerpt: article.excerpt,
      content: article.content,
      tags: article.tags ? article.tags.join(', ') : '',
      isBreaking: article.isBreaking || false,
      imageUrl: article.image || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete article
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        await deleteArticle(id);
        setStatus('✅ Article deleted successfully!');
        loadArticles();
      } catch (error) {
        setStatus('❌ Error deleting article: ' + error.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      author: 'Kohla News Team',
      excerpt: '',
      content: '',
      tags: '',
      isBreaking: false,
      imageUrl: ''
    });
    setEditingId(null);
  };

  // Quill toolbar options
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image', 'video'],
      ['clean']
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">
            {editingId ? 'Edit Article' : 'Write New Article'}
          </h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800 transition">
            ← Back to Site
          </Link>
        </div>

        {/* Status Message */}
        {status && (
          <div className={`p-4 rounded-lg mb-6 ${
            status.includes('✅') ? 'bg-green-100 text-green-700' : 
            status.includes('❌') ? 'bg-red-100 text-red-700' : 
            'bg-yellow-100 text-yellow-700'
          }`}>
            {status}
          </div>
        )}

        {/* Article Form */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter article title..."
                required
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Category</option>
                <option value="Breaking News">Breaking News</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Politics">Politics</option>
                <option value="Health">Health</option>
                <option value="Education">Education</option>
                <option value="Sports">Sports</option>
                <option value="Business">Business</option>
                <option value="Weather">Weather</option>
                <option value="International">International</option>
                <option value="Local Okara">Local Okara</option>
                <option value="Punjab">Punjab</option>
                <option value="Pakistan">Pakistan</option>
              </select>
            </div>

            {/* Author */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">Author</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Breaking News */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                name="isBreaking"
                checked={formData.isBreaking}
                onChange={handleInputChange}
                className="w-5 h-5 text-blue-900"
              />
              <label className="text-gray-700 font-medium">Breaking News</label>
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Tags (comma separated)
              </label>
              <input
                type="text"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Agriculture, Economy, Okara"
              />
            </div>

            {/* Excerpt */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Excerpt (150-160 characters)
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                maxLength="160"
                placeholder="Brief summary of the article..."
                required
              />
              <div className="text-right text-sm text-gray-500">
                {formData.excerpt.length}/160
              </div>
            </div>

            {/* Image URL - Paste from ImgBB */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">
                Image URL (paste from ImgBB)
              </label>
              <div className="flex flex-col gap-2">
                <input
                  type="text"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Paste image URL from ImgBB (e.g., https://i.ibb.co/xxxxx/image.jpg)"
                />
                <div className="text-sm text-gray-500">
                  💡 Upload images at: <a href="https://imgbb.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://imgbb.com</a>
                  {' '}→ After upload, copy the "Direct Link" URL
                </div>
                {formData.imageUrl && (
                  <div className="mt-2 w-32 h-32 rounded-lg overflow-hidden border">
                    <img src={formData.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            {/* Content Editor */}
            <div className="md:col-span-2">
              <label className="block text-gray-700 font-medium mb-2">Content</label>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                className="h-64 mb-12"
                placeholder="Write your article content here..."
              />
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            <button
              type="submit"
              className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold transition"
            >
              {editingId ? 'Update Article' : 'Publish Article'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition"
              >
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        {/* Articles List */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-blue-900 mb-4">Published Articles</h2>
          {articles.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No articles published yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 text-sm font-semibold text-gray-600">Title</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-600">Category</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-600">Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article) => (
                    <tr key={article.id} className="border-t hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {article.isBreaking && (
                            <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded">BREAKING</span>
                          )}
                          <span className="font-medium text-gray-800">{article.title}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {article.category}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-gray-600">{article.date}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(article)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm transition"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(article.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;